import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ChevronDown, CircleHelp, RefreshCw, Stethoscope, XCircle, Zap } from 'lucide-react';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import {
  whatsappAccountService, WhatsappAccount, BotDiagnostics, BotCheckStatus, BotFixAction, BotOutcomeCode, BotOutcomeRecord,
} from '@/services/whatsapp-account.service';

const OUTCOME_LABEL: Record<BotOutcomeCode, string> = {
  replied: 'Replied',
  inbound_failed: 'Message could not be processed',
  flow_handled: 'Handled by a bot flow',
  account_inactive: 'Number not in use',
  bot_off: 'Bot switched off',
  no_flow_no_fallback: 'No flow matched and AI fallback is off',
  media_unsupported: 'Message had no text to read',
  rate_limited: 'Customer hit the message limit',
  lead_paused: 'AI paused for this chat',
  lead_closed: 'Lead is won, lost or spam',
  window_closed: '24-hour window closed',
  opted_out: 'Customer opted out',
  turn_cap: 'AI turn limit reached',
  injection_refused: 'Refused a prompt-injection attempt',
  no_business_profile: 'Business profile missing',
  cost_cap: 'AI cost cap reached',
  ai_failed: 'AI call failed',
  empty_reply: 'AI returned no reply',
  send_failed: 'WhatsApp refused the send',
};
const REPLIED: BotOutcomeCode[] = ['replied', 'flow_handled', 'injection_refused'];
const FAILED: BotOutcomeCode[] = ['inbound_failed', 'cost_cap', 'ai_failed', 'empty_reply', 'send_failed'];

const STATUS_ICON: Record<BotCheckStatus, React.ReactNode> = {
  ok: <CheckCircle2 size={15} className="text-success" />,
  warn: <AlertTriangle size={15} className="text-warning" />,
  fail: <XCircle size={15} className="text-danger" />,
  info: <CircleHelp size={15} className="text-text-muted" />,
};

const LINKS: Partial<Record<BotFixAction, { to: string; label: string }>> = {
  open_ai_settings: { to: '/settings/ai', label: 'AI settings' },
  open_ai_usage: { to: '/settings/ai/usage', label: 'Cost & credits' },
  open_business_profile: { to: '/settings/business-profile', label: 'Business profile' },
  open_accounts: { to: '/whatsapp-accounts?tab=accounts', label: 'Accounts' },
};

function ago(iso?: string): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

interface Props {
  account: WhatsappAccount;
  /** Server copy after a fix (activate / switch to AI chat) so the page reflects it. */
  onAccountChange: (updated: WhatsappAccount) => void;
}

/** "Why isn't the bot replying?" — live checks plus what happened to the latest messages. */
export function BotHealthCard({ account, onAccountChange }: Props) {
  const [data, setData] = useState<BotDiagnostics | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fixing, setFixing] = useState<BotFixAction | null>(null);
  const [showPassed, setShowPassed] = useState(false);

  const run = useCallback(async (live = false) => {
    live ? setTesting(true) : setLoading(true);
    try {
      setData(await whatsappAccountService.botDiagnostics(account.accountUid, live));
    } catch { /* axios interceptor toasts */ } finally {
      live ? setTesting(false) : setLoading(false);
    }
  }, [account.accountUid]);

  useEffect(() => { void run(false); }, [run, account.botMode, account.isActive]);

  const fix = async (action: BotFixAction) => {
    if (action === 'test_ai') return run(true);
    setFixing(action);
    try {
      if (action === 'activate') {
        onAccountChange(await whatsappAccountService.setActive(account.accountUid, true));
        toast.success('This number is now in use');
      } else if (action === 'use_ai_chat') {
        onAccountChange(await whatsappAccountService.updateProfileById(account.accountUid, { botMode: 'ai_chat' }));
        toast.success('AI chat is on for this number');
      } else if (action === 'subscribe_webhook') {
        const r = await whatsappAccountService.subscribeWebhook(account.accountUid);
        if (!r.subscribedNow) toast.error(r.error || 'Meta did not accept the webhook repair');
        else toast.success('Webhook repaired — send a test message');
        await run(false);
      }
    } catch { /* axios interceptor toasts */ } finally { setFixing(null); }
  };

  const problems = data?.checks.filter(c => c.status === 'fail' || c.status === 'warn') ?? [];
  const pending = data?.checks.filter(c => c.status === 'info') ?? [];
  const passed = data?.checks.filter(c => c.status === 'ok') ?? [];
  const blocking = problems.filter(c => c.status === 'fail').length;

  const today = useMemo(() => {
    const counts = data?.activity.today ?? {};
    const sum = (codes: BotOutcomeCode[]) => codes.reduce((n, code) => n + (counts[code] ?? 0), 0);
    const replied = sum(REPLIED), failed = sum(FAILED);
    const total = Object.values(counts).reduce((n, v) => n + (v ?? 0), 0);
    const topSkip = (Object.entries(counts) as Array<[BotOutcomeCode, number]>)
      .filter(([code]) => !REPLIED.includes(code) && !FAILED.includes(code))
      .sort((a, b) => b[1] - a[1])[0];
    return { replied, failed, skipped: total - replied - failed, topSkip };
  }, [data]);

  const verdict = !data ? null
    : blocking ? { tone: 'danger', text: `${blocking} problem${blocking > 1 ? 's' : ''} stop${blocking > 1 ? '' : 's'} the bot from replying` }
    : problems.length ? { tone: 'warning', text: `The bot replies, with ${problems.length} thing${problems.length > 1 ? 's' : ''} to check` }
    : pending.length ? { tone: 'muted', text: 'Settings look right — test the AI key to be sure' }
    : { tone: 'success', text: 'The bot can reply on this number' };

  const modeLabel = data?.botMode === 'ai_chat' ? 'AI chat' : data?.botMode === 'off' ? 'Off' : 'Bot flows';
  const route = data?.ai ? `${data.ai.provider} · ${data.ai.model} · ${data.ai.scope === 'company' ? 'your key' : 'managed credits'}` : null;

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Stethoscope size={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold text-text-primary">Bot health</div>
          <div className="truncate text-2xs text-text-muted">{modeLabel}{route ? ` · ${route}` : ''}</div>
        </div>
        <Button size="sm" variant="secondary" iconLeft={<Zap size={13} />} loading={testing} disabled={!data?.ai || loading} onClick={() => run(true)}>
          Test AI now
        </Button>
        <button type="button" onClick={() => run(data?.ai?.tested === true)} disabled={loading || testing} aria-label="Re-run checks"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {!data ? (
        <div className="space-y-2 border-t border-border px-4 py-3"><div className="skeleton h-4 w-1/2 rounded" /><div className="skeleton h-10 rounded-lg" /></div>
      ) : (
        <>
          <div className={clsx('flex items-center gap-2 border-y px-4 py-2 text-xs font-semibold', {
            'border-danger/20 bg-danger-soft text-danger': verdict?.tone === 'danger',
            'border-warning/30 bg-warning-soft text-warning': verdict?.tone === 'warning',
            'border-success/20 bg-success-soft text-success': verdict?.tone === 'success',
            'border-border bg-surface-alt text-text-secondary': verdict?.tone === 'muted',
          })}>
            {verdict?.tone === 'danger' ? <XCircle size={14} /> : verdict?.tone === 'warning' ? <AlertTriangle size={14} /> : verdict?.tone === 'success' ? <CheckCircle2 size={14} /> : <CircleHelp size={14} />}
            {verdict?.text}
          </div>

          <dl className="grid grid-cols-2 divide-x divide-y divide-border border-b border-border text-xs sm:grid-cols-4 sm:divide-y-0">
            <Stat label="Last message in" value={ago(data.activity.lastInboundAt ?? data.activity.lastWebhookAt)} />
            <Stat label="Last bot reply" value={ago(data.activity.lastReplyAt)} tone={data.activity.lastInboundAt && !data.activity.lastReplyAt ? 'danger' : undefined} />
            <Stat label="Replied today" value={String(today.replied)} tone={today.replied ? 'success' : undefined} />
            <Stat label="No reply today" value={String(today.skipped + today.failed)} tone={today.failed ? 'danger' : today.skipped ? 'warning' : undefined}
              hint={today.topSkip ? `Mostly: ${OUTCOME_LABEL[today.topSkip[0]]}` : today.failed ? `${today.failed} failed` : undefined} />
          </dl>

          <ul className="divide-y divide-border">
            {[...problems, ...pending].map(check => (
              <CheckRow key={check.key} status={check.status} title={check.title} detail={check.detail}
                action={check.fix ? <FixButton action={check.fix} busy={fixing === check.fix || (check.fix === 'test_ai' && testing)} onFix={fix} /> : null} />
            ))}
            {data.activity.lastError && <LastProblem record={data.activity.lastError} />}
            {passed.length > 0 && (
              <li>
                <button type="button" onClick={() => setShowPassed(v => !v)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs text-text-muted hover:bg-surface-alt/60">
                  <CheckCircle2 size={14} className="text-success" />
                  <span className="flex-1">{passed.length} check{passed.length > 1 ? 's' : ''} passed</span>
                  <ChevronDown size={14} className={clsx('transition-transform', showPassed && 'rotate-180')} />
                </button>
                {showPassed && (
                  <ul className="divide-y divide-border border-t border-border bg-surface-alt/30">
                    {passed.map(check => <CheckRow key={check.key} status="ok" title={check.title} detail={check.detail} />)}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </>
      )}
    </Card>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: string; tone?: 'success' | 'warning' | 'danger'; hint?: string }) {
  return (
    <div className="min-w-0 px-4 py-2">
      <dt className="text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{label}</dt>
      <dd className={clsx('mt-0.5 truncate text-sm font-semibold tabular-nums', tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : 'text-text-primary')}>{value}</dd>
      {hint && <dd className="truncate text-2xs text-text-muted" title={hint}>{hint}</dd>}
    </div>
  );
}

function CheckRow({ status, title, detail, action }: { status: BotCheckStatus; title: string; detail: string; action?: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 px-4 py-2.5">
      <span className="mt-0.5 shrink-0">{STATUS_ICON[status]}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-tight text-text-primary">{title}</div>
        <div className="mt-0.5 text-xs leading-snug text-text-muted">{detail}</div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </li>
  );
}

function FixButton({ action, busy, onFix }: { action: BotFixAction; busy: boolean; onFix: (a: BotFixAction) => void }) {
  const link = LINKS[action];
  if (link) return <Link to={link.to} className="inline-flex h-7 items-center rounded-lg border border-border px-2.5 text-xs font-medium text-text-secondary hover:border-primary/50 hover:text-primary">{link.label}</Link>;
  const label = action === 'activate' ? 'Use this number' : action === 'use_ai_chat' ? 'Switch to AI chat' : action === 'subscribe_webhook' ? 'Repair webhook' : 'Test now';
  return <Button size="sm" variant={action === 'test_ai' ? 'secondary' : 'primary'} loading={busy} onClick={() => onFix(action)}>{label}</Button>;
}

function LastProblem({ record }: { record: BotOutcomeRecord }) {
  return (
    <li className="flex items-start gap-2.5 bg-danger-soft/40 px-4 py-2.5">
      <XCircle size={15} className="mt-0.5 shrink-0 text-danger" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-tight text-text-primary">
          Last failure · {OUTCOME_LABEL[record.code] ?? record.code}
          <span className="ml-1.5 font-normal text-text-muted">{ago(record.at)}</span>
        </div>
        {record.detail && <div className="mt-0.5 break-words text-xs leading-snug text-text-muted">{record.detail}</div>}
      </div>
    </li>
  );
}
